import css from './Step2SelectItems.module.css';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SelectionItemsHeaderMobile } from './SelectionItemsHeaderMobile/SelectionItemsHeaderMobile';
import { SelectionItemsList } from './SelectionItemsList/SelectionitemsList';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import { useProjectStore } from '../../../../Stores/ProjectStore';
import { BasicLoader } from '@shared/Ui/Loaders/BasicLoader/BasicLoader';
import { PaginationButtons } from '@shared/Widgets/PaginationButtons/PaginationButtons';
import { SortParamEnum, SortParamType } from '@shared/Enums/SortParamEnum';
import { ROUTES } from '@configs/AppRoutes';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import { GetItemTypeById } from '@viewModels/ItemTypeViewModel/Services/GetItemTypeById';
import { useFragmentsStore } from '../../../../Stores/FragmentsStore';
import { ItemTypeViewModel } from '@viewModels/ItemTypeViewModel/ItemTypeViewModel';
import { useItemTypeStore } from '../../../../Stores/ItemTypeStore';
import { notifyErrorCustom } from '@shared/Widgets/ToastNotifications/ToastNotifications';
import { GetFragmentByItemTypeId } from '@modules/Steps/Shared/Helpers/GetFragmentByItemTypeId';
import { FragmentsViewModel } from '@viewModels/FragmentsViewModel/FragmentsViewModel';

const Step2SelectItems: React.FC = () => {
    const { customProject, setIsMobile } = useProjectStore();

    const { parsedFragments } = useFragmentsStore();
    const { isItemsLoading, itemsData } = useItemTypeStore();
    const itemTypeViewModel = new ItemTypeViewModel();
    const fragmentsViewModel = new FragmentsViewModel();

    const { projectId, itemTypeId, viewMode } = useParams();

    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const page = parseInt(searchParams.get('page') || '1', 10);
    const sortOrder =
        (searchParams.get('sortOrder') as SortParamType) || SortParamEnum.ASC;

    const itemType = GetItemTypeById(Number(itemTypeId), parsedFragments);

    const isFillDisabled =
        itemTypeViewModel.calculateTotalQuantityFromItemType(itemType) >=
        itemType.qty;

    useEffect(() => {
        const fetchData = async () => {
            await itemTypeViewModel.fetchItemType({
                itemType: itemType,
                page: page,
                perPage: 5,
                search,
                sortOrder,
                projectStoreId: customProject.storeId,
            });
        };

        fetchData();
    }, [searchParams, search]);

    const handleChangeSearch = (value: string) => void setSearch(value);

    const handleChangeSortOrder = (sortBy: SortParamType) =>
        void setSearchParams({ sortOrder: sortBy });

    const handleChangePage = (newPage: number) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('page', newPage.toString());
            return newParams;
        });
    };

    const handleConfirmSelection = async () => {
        if (viewMode === StepsViewModeEnum.PRESENTATION) {
            navigate(ROUTES.STEP2.getPath(StepsViewModeEnum.PRESENTATION));
        } else {
            if (!itemType) {
                notifyErrorCustom('Failed to confirm selection');
                return;
            }
            setIsMobile(true);
            itemTypeViewModel.updateItemTypeItems(itemType);
            const currentFragment = GetFragmentByItemTypeId(
                Number(itemTypeId),
                parsedFragments
            );

            if (!currentFragment) {
                notifyErrorCustom('Failed to confirm selection');
                return;
            }

            const response = await fragmentsViewModel.updateParsedFragment(
                currentFragment.uuid
            );
            if (response === false) {
                notifyErrorCustom('Failed to confirm selection');
                return;
            }
            navigate(
                ROUTES.STEP2.getPath(StepsViewModeEnum.PROJECT, projectId)
            );
        }
    };

    return (
        <main className={css.wrapper}>
            <SelectionItemsHeaderMobile
                itemType={itemType}
                searchValue={search}
                changeSearchValue={handleChangeSearch}
                changeSortBy={handleChangeSortOrder}
            />
            {isItemsLoading ? (
                <BasicLoader />
            ) : itemsData.items && itemsData.items.length > 0 ? (
                <>
                    <SelectionItemsList
                        items={itemsData.items}
                        itemType={itemType}
                        isFillDisabled={isFillDisabled}
                    />
                    <div className={css.modalPaginationControlsContainer}>
                        <PaginationButtons
                            changePage={handleChangePage}
                            pagination={itemsData.pagination}
                        />
                    </div>
                </>
            ) : (
                <div className={css.noItemsMessage}>No items were found</div>
            )}

            <BasicButton
                onClick={handleConfirmSelection}
                disabled={isItemsLoading}
                extraClassName={css.confirmButton}
            >
                Confirm selection
            </BasicButton>
        </main>
    );
};

export default React.memo(Step2SelectItems);
